/**
 * React Query bindings for the tool module. A tool write can also change its
 * agent's config (see `tool.service.ts`), so the mutations that do — every
 * add, and an edit or delete of a native tool — refresh the agent too.
 */

import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { ListParams, PagedResult } from "@/lib/list-query";
import { agentKeys, notFoundIsFinal } from "./assistant.query";
import {
  type AgentTool,
  createCustomTool,
  createNativeTool,
  deleteTool,
  listNativeTools,
  listTools,
  type UpdateCustomToolBody,
  type UpdateNativeToolBody,
  updateCustomTool,
  updateNativeTool,
} from "./tool.service";

export type { AgentTool } from "./tool.service";
export { TOOLS_PAGE_SIZE } from "./tool.service";

export const toolKeys = {
  /** Every cached read of one agent's tools. A write invalidates this prefix,
   * which covers the page on screen and the dialog's built-ins alike. */
  all: (agentId?: string) => ["tools", agentId] as const,
  page: (agentId: string | undefined, params: ListParams) =>
    ["tools", agentId, "page", params] as const,
  natives: (agentId?: string) => ["tools", agentId, "native"] as const,
};

function invalidate(queryClient: QueryClient, tool: AgentTool, agentChanged: boolean) {
  queryClient.invalidateQueries({ queryKey: toolKeys.all(tool.agent_id) });
  if (!agentChanged) return;
  queryClient.invalidateQueries({ queryKey: agentKeys.detail(tool.agent_id) });
  queryClient.invalidateQueries({ queryKey: ["agents"] });
}

/**
 * One page of the agent's tools. `/agents/new` has no agent yet, so there is
 * nothing to ask for.
 *
 * `placeholderData` keeps the current page rendered while the next one loads,
 * so paging does not blank the panel.
 */
export const useTools = (agentId: string | undefined, params: ListParams) =>
  useQuery<PagedResult<AgentTool>>({
    queryKey: toolKeys.page(agentId, params),
    queryFn: () => listTools(agentId!, params),
    enabled: !!agentId && agentId !== "new",
    placeholderData: keepPreviousData,
  });

/** The agent's built-ins, whole — what the add dialog needs to know which
 * cards are taken. See `listNativeTools`. A deleted agent's answer is a 404,
 * final as the agent's own read is. */
export const useNativeTools = (agentId?: string) =>
  useQuery<AgentTool[]>({
    queryKey: toolKeys.natives(agentId),
    queryFn: () => listNativeTools(agentId!),
    enabled: !!agentId && agentId !== "new",
    ...notFoundIsFinal,
  });

interface ToolWriteToasts {
  title: string;
  description: string;
  failed: string;
}

const CREATED: ToolWriteToasts = {
  title: "Tool created",
  description: "Your tool has been created successfully.",
  failed: "Failed to create tool",
};

const UPDATED: ToolWriteToasts = {
  title: "Tool updated",
  description: "Your tool has been updated successfully.",
  failed: "Failed to update tool",
};

/** An add or an edit: refreshes the agent's tools — and the agent, when the
 * write changed its config too — and toasts the outcome. */
function useToolWrite<Vars>(
  write: (vars: Vars) => Promise<AgentTool>,
  toasts: ToolWriteToasts,
  agentChanged: boolean,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: write,
    onSuccess: (tool: AgentTool) => {
      invalidate(queryClient, tool, agentChanged);
      toast({ title: toasts.title, description: toasts.description });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: toasts.failed, description: error.message });
    },
  });
}

export const useCreateCustomTool = () => useToolWrite(createCustomTool, CREATED, true);

export const useCreateNativeTool = () => useToolWrite(createNativeTool, CREATED, true);

/** A custom edit touches the row only; the agent's config is unchanged. */
export const useUpdateCustomTool = () =>
  useToolWrite(
    ({ tool, updates }: { tool: AgentTool; updates: UpdateCustomToolBody }) =>
      updateCustomTool(tool.id, updates),
    UPDATED,
    false,
  );

export const useUpdateNativeTool = () =>
  useToolWrite(
    ({ tool, updates }: { tool: AgentTool; updates: UpdateNativeToolBody }) =>
      updateNativeTool(tool.id, updates),
    UPDATED,
    true,
  );

export const useDeleteTool = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (tool: AgentTool) => deleteTool(tool.id),
    onSuccess: (_, tool) => {
      invalidate(queryClient, tool, !!tool.is_native);
      toast({
        title: "Tool deleted",
        description: "Your tool has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to delete tool",
        description: error.message,
      });
    },
  });
};
